import { NgClass } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { FILE_OBJECT_TYPE_COMMUNICATION_BACKOFFICE } from 'types/file.interface';
import {
  BackofficeReadingRoomSubmissionCard,
  CreateReadingRoomSubmissionRequestAdditionalInfoDto,
  ReadingRoomSubmissionCommunicationDto
} from 'types/service.interface';
import { getControlsWithErrors, MAX_LENGTH_ADDITIONAL_INFO } from 'util/form.utility';
import { BaseFormModalComponent } from '../base-form-modal.component';
@Component({
  selector: 'app-request-additional-info-modal',
  standalone: true,
  imports: [TranslateModule, SpinnerComponent, NgClass, TooltipComponent, ReactiveFormsModule, FileUploadComponent],
  templateUrl: './request-additional-info-modal.component.html',
  styleUrls: ['./request-additional-info-modal.component.scss', '../../form/base-form.scss']
})
export class RequestAdditionalInfoModalComponent extends BaseFormModalComponent implements AfterViewInit, OnChanges {
  public readonly maxFieldLength = MAX_LENGTH_ADDITIONAL_INFO;
  @Input() isOpen!: boolean;
  @Input() allowFiles: boolean = true;
  @Input() requestedAdditionalInformation: boolean = true;
  @Input() modalTitle: string = '';
  @Input() tooltipText: string = 'requestAppAdditionInfoBackoffice';
  @Input() application!: BackofficeReadingRoomSubmissionCard;
  @Output() update: EventEmitter<ReadingRoomSubmissionCommunicationDto> = new EventEmitter<ReadingRoomSubmissionCommunicationDto>();
  @ViewChild('content') content!: TemplateRef<any>;
  isSubmittingFiles = false;
  note: ReadingRoomSubmissionCommunicationDto | null = null;
  override fileUploadComponentCount = 1;
  readonly communicationFileType = FILE_OBJECT_TYPE_COMMUNICATION_BACKOFFICE;
  isCrudLoading = false;
  shouldRenderTooltip = false;

  get backofficeText(): AbstractControl<any, any> | null {
    return this.form.get('backofficeText');
  }

  constructor(
    formBuilder: FormBuilder,
    config: NgbModalConfig,
    modalService: NgbModal,
    toastService: ToastService,
    translateService: TranslateService,
    private apiService: ApiService
  ) {
    super(formBuilder, config, modalService, toastService, translateService);
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.fillForm();
      this.open(this.content);
    }
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.fillForm();
      this.shouldRenderTooltip = false;
      this.open(this.content);
      setTimeout(() => {
        this.shouldRenderTooltip = true;
      }, 10);
    }
  }

  fillForm(): void {
    this.form = this.formBuilder.group({
      backofficeText: ['', [Validators.required, Validators.maxLength(MAX_LENGTH_ADDITIONAL_INFO)]]
    });
  }

  protected override close(): void {
    super.close();
  }

  submit(): void {
    if (this.form.valid) {
      this.isCrudLoading = true;
      const args: CreateReadingRoomSubmissionRequestAdditionalInfoDto = {
        ...this.form.value,
        readingRoomSubmissionId: this.application.id,
        requestedAdditionalInformation: this.requestedAdditionalInformation
      };

      this.apiService.createApplicationInfoRequest(args).subscribe((response: ReadingRoomSubmissionCommunicationDto) => {
        this.toastService.showSuccess('requestAppClientNoteSuccess', '', {
          shouldTranslate: true
        });
        this.note = response;
        this.isSubmittingFiles = true;

        if (!this.allowFiles) {
          this.onSubmitSuccess();
        }
      });
      this.isCrudLoading = false;
    } else {
      this.submitAttempted = true;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'components.requestAdditionalNotes');
    }
  }

  protected override onSubmitSuccess(): void {
    this.isSubmittingFiles = false;
    this.fileActions = [];
    this.uploadComponentsReceived = 0;
    this.close();

    if (this.note) {
      this.update.emit(this.note);
    }
  }
}
